import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HeartPulse, Award, CheckCircle2, XCircle, ArrowRight, Printer, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  super_admin: "👑 Admin KWIKILA Care",
  hospital_admin: "🏥 Admin de l'hôpital",
  doctor: "🩺 Médecin",
  nurse: "💉 Infirmier(e)",
  secretary: "📋 Secrétaire médical(e)",
  ministry: "🏛️ Compte Ministère",
};

export default function FormationQuiz() {
  const { role } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | quiz | result
  const [quiz, setQuiz] = useState(null);
  const [form, setForm] = useState({ trainee_name: "", trainee_email: "", trainee_hospital: "" });
  const [answers, setAnswers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/formation/quiz/${role}`)
      .then(r => { setQuiz(r.data); setAnswers(new Array(r.data.questions.length).fill(-1)); })
      .catch(() => toast.error("Rôle inconnu"));
  }, [role]);

  const start = (e) => {
    e.preventDefault();
    if (!form.trainee_name.trim()) return toast.error("Nom requis");
    setStep("quiz");
  };

  const submit = async () => {
    if (answers.some(a => a === -1)) return toast.error("Répondez à toutes les questions");
    try {
      const r = await axios.post(`${API}/formation/quiz/attempt`, { role, ...form, answers });
      setResult(r.data);
      setStep("result");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  if (!quiz) return <div className="min-h-screen grid place-items-center text-[#5C6B61]">Chargement…</div>;

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-6" data-testid="quiz-page">
      <div className="max-w-2xl mx-auto">
        <Button variant="outline" size="sm" onClick={() => navigate("/formation")} className="mb-4" data-testid="quiz-back">
          <ArrowLeft className="w-4 h-4 mr-1.5"/> Guide de formation
        </Button>

        {/* Header */}
        <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#1E4631] grid place-items-center">
              <Award className="w-6 h-6 text-white"/>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#D96C4A] font-semibold">Certification KWIKILA Care</div>
              <div className="font-[Outfit] font-bold text-xl">{ROLE_LABELS[role] || role}</div>
            </div>
          </div>
          <p className="text-sm text-[#5C6B61] mt-3">
            5 questions · réussite à {quiz.pass_threshold}/5 · certificat officiel PDF si validé
          </p>
        </div>

        {/* STEP 1 — Trainee info */}
        {step === "form" && (
          <form onSubmit={start} className="bg-white rounded-2xl border border-[#E5EBE7] p-6 space-y-4" data-testid="trainee-form">
            <h2 className="font-[Outfit] font-bold text-lg">Vos informations</h2>
            <div><Label>Nom complet *</Label><Input required value={form.trainee_name} onChange={e=>setForm({...form,trainee_name:e.target.value})} placeholder="Ex: Dr. Jean MUKENDI" data-testid="trainee-name"/></div>
            <div><Label>Email (facultatif)</Label><Input type="email" value={form.trainee_email} onChange={e=>setForm({...form,trainee_email:e.target.value})} data-testid="trainee-email"/></div>
            <div><Label>Hôpital / Organisation</Label><Input value={form.trainee_hospital} onChange={e=>setForm({...form,trainee_hospital:e.target.value})} placeholder="Ex: Hôpital Saint-Joseph Kinshasa" data-testid="trainee-hospital"/></div>
            <Button type="submit" className="w-full bg-[#1E4631] hover:bg-[#163424]" data-testid="start-quiz">
              Démarrer le quiz <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          </form>
        )}

        {/* STEP 2 — Quiz */}
        {step === "quiz" && (
          <div className="bg-white rounded-2xl border border-[#E5EBE7] p-6" data-testid="quiz-question">
            <div className="text-xs text-[#5C6B61] mb-2">Question {current + 1} / {quiz.questions.length}</div>
            <div className="h-1.5 bg-[#E5EBE7] rounded-full mb-4">
              <div className="h-full bg-[#D96C4A] rounded-full transition-all" style={{ width: `${((current+1)/quiz.questions.length)*100}%` }}/>
            </div>
            <h3 className="font-[Outfit] font-semibold text-lg mb-4">{quiz.questions[current].q}</h3>
            <div className="space-y-2">
              {quiz.questions[current].opts.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => { const na = [...answers]; na[current] = i; setAnswers(na); }}
                  className={`w-full text-left p-3 rounded-lg border-2 transition ${answers[current] === i ? "border-[#1E4631] bg-[#1E4631]/5" : "border-[#E5EBE7] hover:border-[#1E4631]/40"}`}
                  data-testid={`quiz-opt-${current}-${i}`}
                >
                  {opt}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0}>Précédent</Button>
              {current < quiz.questions.length - 1 ? (
                <Button onClick={() => setCurrent(c => c + 1)} disabled={answers[current] === -1} className="bg-[#1E4631] hover:bg-[#163424]" data-testid="quiz-next">Suivant</Button>
              ) : (
                <Button onClick={submit} disabled={answers.some(a => a === -1)} className="bg-[#D96C4A] hover:bg-[#B85A3D]" data-testid="quiz-submit">Valider le quiz</Button>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 — Result */}
        {step === "result" && result && (
          <div className="bg-white rounded-2xl border border-[#E5EBE7] p-8 text-center" data-testid="quiz-result">
            {result.passed ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-[#2E7D32] mx-auto mb-3"/>
                <h2 className="font-[Outfit] font-bold text-2xl text-[#2E7D32]">Bravo, vous avez réussi ! 🎉</h2>
                <p className="text-lg mt-2">Score : <strong>{result.score}/{result.total}</strong></p>
                <p className="text-sm text-[#5C6B61] mt-2">Votre certificat officiel KWIKILA Care est prêt.</p>
                <div className="mt-6 flex gap-3 justify-center">
                  <Link to={`/formation/certificat/${result.certificate_id}`} target="_blank">
                    <Button className="bg-[#1E4631] hover:bg-[#163424]" data-testid="view-certificate">
                      <Award className="w-4 h-4 mr-1.5"/> Voir mon certificat
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-[#C62828] mx-auto mb-3"/>
                <h2 className="font-[Outfit] font-bold text-2xl">Score insuffisant</h2>
                <p className="text-lg mt-2">Score : <strong>{result.score}/{result.total}</strong> (minimum {quiz.pass_threshold} requis)</p>
                <p className="text-sm text-[#5C6B61] mt-2">Relisez le guide et retentez votre chance.</p>
                <div className="mt-6 flex gap-3 justify-center">
                  <Link to="/formation"><Button variant="outline">Retour au guide</Button></Link>
                  <Button className="bg-[#D96C4A]" onClick={() => { setStep("form"); setCurrent(0); setAnswers(new Array(quiz.questions.length).fill(-1)); }}>
                    Refaire le quiz
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============ CERTIFICATE PAGE ============ */
export function FormationCertificate() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/formation/certificate/${cid}`)
      .then(r => setCert(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Certificat introuvable"));
  }, [cid]);

  if (err) return <div className="min-h-screen grid place-items-center text-[#C62828]">{err}</div>;
  if (!cert) return <div className="min-h-screen grid place-items-center text-[#5C6B61]">Chargement…</div>;
  if (!cert.passed) return <div className="min-h-screen grid place-items-center text-[#C62828]">Ce certificat n'est pas valide (quiz non réussi).</div>;

  const issued = new Date(cert.issued_at);

  return (
    <div className="min-h-screen bg-white" data-testid="certificate-page">
      <div className="print:hidden border-b border-[#E5EBE7] px-6 py-3 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1.5"/> Retour</Button>
        <div className="text-xs text-[#5C6B61]">Certificat officiel A4 · Ctrl+P → PDF</div>
        <Button onClick={() => window.print()} className="bg-[#1E4631]" size="sm" data-testid="cert-print">
          <Printer className="w-4 h-4 mr-1.5"/> Imprimer / PDF
        </Button>
      </div>

      <div className="max-w-[297mm] mx-auto p-16 bg-white" style={{ minHeight: "210mm" }}>
        {/* Border decorative */}
        <div className="border-[6px] border-double border-[#1E4631] p-12 relative">
          <div className="absolute -top-3 -left-3 w-6 h-6 rounded-full bg-[#D96C4A]"/>
          <div className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-[#D96C4A]"/>
          <div className="absolute -bottom-3 -left-3 w-6 h-6 rounded-full bg-[#D96C4A]"/>
          <div className="absolute -bottom-3 -right-3 w-6 h-6 rounded-full bg-[#D96C4A]"/>

          <div className="text-center">
            <div className="flex justify-center gap-3 items-center mb-3">
              <div className="w-14 h-14 rounded-2xl bg-[#1E4631] grid place-items-center">
                <HeartPulse className="w-8 h-8 text-white"/>
              </div>
              <div className="text-left">
                <div className="font-[Outfit] font-bold text-2xl text-[#1E4631]">KWIKILA Care</div>
                <div className="text-xs text-[#5C6B61]">La santé en confiance · RDC</div>
              </div>
            </div>

            <div className="text-xs uppercase tracking-[0.3em] text-[#D96C4A] font-semibold mt-6">Certificat de formation</div>
            <h1 className="font-[Outfit] font-bold text-5xl mt-2 text-[#1E4631]">Attestation officielle</h1>

            <p className="text-lg text-[#5C6B61] mt-8">Nous certifions par la présente que</p>
            <div className="my-4 py-4 border-y-2 border-[#E5EBE7]">
              <div className="font-[Outfit] font-bold text-4xl text-[#1A1D1A]">{cert.trainee_name}</div>
              {cert.trainee_hospital && <div className="text-sm text-[#5C6B61] mt-1">{cert.trainee_hospital}</div>}
            </div>

            <p className="text-lg text-[#5C6B61] mt-6">a validé avec succès le module de formation</p>
            <div className="font-[Outfit] font-bold text-2xl text-[#D96C4A] mt-3">
              {ROLE_LABELS[cert.role] || cert.role}
            </div>

            <div className="flex items-center justify-center gap-2 mt-6">
              <Sparkles className="w-5 h-5 text-[#E3A72F]"/>
              <span className="text-lg font-semibold">Score : {cert.score}/{cert.total}</span>
              <Sparkles className="w-5 h-5 text-[#E3A72F]"/>
            </div>

            <p className="text-sm text-[#5C6B61] mt-10 max-w-2xl mx-auto italic">
              Le titulaire est reconnu apte à exercer les fonctions liées à ce rôle sur la plateforme KWIKILA Care
              dans le respect des droits, interdictions et procédures définis par le guide officiel.
            </p>

            <div className="grid grid-cols-3 gap-8 mt-12 pt-6 border-t border-[#E5EBE7] text-xs">
              <div>
                <div className="uppercase tracking-widest text-[10px] text-[#5C6B61] mb-1">Date</div>
                <div className="font-semibold text-sm">{issued.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[10px] text-[#5C6B61] mb-1">N° de certificat</div>
                <div className="font-mono text-[10px] break-all">{cert.id}</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-[10px] text-[#5C6B61] mb-1">Vérification</div>
                <div className="font-mono text-[9px] break-all">kwikilacare.com/formation/certificat/{cert.id.slice(0, 8)}…</div>
              </div>
            </div>

            <div className="mt-8 flex justify-around text-xs">
              <div>
                <div className="h-16 border-b border-[#5C6B61] w-48 mx-auto"/>
                <div className="text-[#5C6B61] mt-1">Formateur</div>
              </div>
              <div className="flex flex-col items-center">
                <HeartPulse className="w-10 h-10 text-[#1E4631]"/>
                <div className="text-[9px] text-[#5C6B61] mt-1">Sceau KWIKILA Care</div>
              </div>
              <div>
                <div className="h-16 border-b border-[#5C6B61] w-48 mx-auto"/>
                <div className="text-[#5C6B61] mt-1">KWIKILA Care</div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center text-[9px] text-[#8A9990] mt-6">
          Ce certificat est authentique et vérifiable en ligne. contact@kwikilacare.com · +243 824 314 459
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 0; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
